/**
 * Link Validator API - Client-side validation for tracking URLs
 * Exposed on window.LinkValidator
 *
 * Supports both:
 * - Auto-read from Redux store (when initialized with store)
 * - Manual data parameter (for standalone use)
 */

// Field mappings (from useValidate.js)
const isValidField = {
  Deep_Link: 'deep',
  Impression_Link: 'impression',
  Tracking_Link: 'tracking',
  VTA_Link: 'vta',
};

const exstraIsValidField = {
  Deep_Link: 'tracking',
  Impression_Link: 'vta',
  Tracking_Link: 'deep',
  VTA_Link: 'impression',
};

const validator_domains_field = {
  Tracking_Link: 'tracking_domains',
  VTA_Link: 'vta_domains',
  Deep_Link: 'deeplink_domains',
  Impression_Link: 'impression_domains',
};

const extra_validator_domains_field = {
  Tracking_Link: 'deeplink_domains',
  VTA_Link: 'impression_domains',
  Deep_Link: 'tracking_domains',
  Impression_Link: 'vta_domains',
};

const VALID_LINK_TYPES = ['Tracking_Link', 'Impression_Link', 'VTA_Link', 'Deep_Link'];

let _store = null;

/**
 * Validate domain format using regex
 * @param {string} domain
 * @returns {boolean}
 */
function validateDomainFormat(domain) {
  const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)+[a-zA-Z0-9]{2,11}?$/;
  return domainRegex.test(domain);
}

/**
 * Get link_validator_data from Redux or use provided data
 * @param {array} [providedData]
 * @returns {array|null}
 */
function getLinkValidatorDataInternal(providedData) {
  if (providedData) return providedData;
  if (_store) {
    return _store.getState()?.creatives?.creativeAndTrackingData?.link_validator_data;
  }
  return null;
}

/**
 * Get s2s_tracking_enabled from Redux or use provided value
 * @param {boolean} [providedValue]
 * @returns {boolean}
 */
function getS2sTrackingEnabledInternal(providedValue) {
  if (providedValue !== undefined) return providedValue;
  if (_store) {
    return _store.getState()?.creatives?.creativeAndTrackingData?.s2s_tracking_enabled;
  }
  return false;
}

/**
 * Get domains array for a specific link type
 * @param {string} linkType
 * @param {array} linkValidatorData
 * @returns {object[]}
 */
function getDomainsObj(linkType, linkValidatorData) {
  if (!linkValidatorData) return [];

  const res = linkValidatorData.flatMap((item) => {
    const validationIsRequire =
      item[validator_domains_field[linkType]] !== null
        ? item[`${isValidField[linkType]}_link_require_validation`]
        : item[`${exstraIsValidField[linkType]}_link_require_validation`];

    const currentDomains =
      item[validator_domains_field[linkType]] === null
        ? item[extra_validator_domains_field[linkType]]
        : item[validator_domains_field[linkType]];

    return currentDomains !== null
      ? currentDomains.map((d) => ({ ...d, validationIsRequire }))
      : null;
  });

  return res.filter(Boolean);
}

/**
 * Find tracking company (tc_number) for a given URL
 * @param {URL} urlObj
 * @param {string} linkType
 * @param {array} linkValidatorData
 * @returns {number|null}
 */
function findTrackingCompany(urlObj, linkType, linkValidatorData) {
  if (!linkValidatorData) return null;

  const currentValidator = linkValidatorData.find((item) => {
    let domainsArr =
      item[validator_domains_field[linkType]] === null
        ? item[extra_validator_domains_field[linkType]]
        : item[validator_domains_field[linkType]];
    domainsArr = domainsArr?.map((domain) => domain.tc_domain_name);
    return domainsArr?.some((domain) => urlObj.host.includes(domain));
  });

  return currentValidator ? currentValidator.tc_number : null;
}

/**
 * Get mandatory parameters for validation
 * @param {string} currentDomain
 * @param {array} linkValidatorData
 * @param {string} linkType
 * @returns {array|null}
 */
function getValidateParams(currentDomain, linkValidatorData, linkType) {
  if (currentDomain === null || !linkValidatorData) return null;

  const currentValidator = linkValidatorData.find((item) => {
    let domainsArr =
      item[validator_domains_field[linkType]] === null
        ? item[extra_validator_domains_field[linkType]]
        : item[validator_domains_field[linkType]];
    domainsArr = domainsArr?.map((domain) => domain.tc_domain_name);
    return domainsArr?.some((link) => link.includes(currentDomain) || currentDomain.includes(link));
  });

  if (!currentValidator) return null;

  const paramsArr =
    currentValidator[validator_domains_field[linkType]] !== null
      ? currentValidator[`${isValidField[linkType]}_params_android`]
      : currentValidator[`${exstraIsValidField[linkType]}_params_android`];

  const paramsArrToCheck = paramsArr
    ?.map((param) => {
      if (param.tcp_is_mandatory) {
        return param.param_values?.map((item) => item.tcp_params);
      }
      return null;
    })
    ?.filter(Boolean);

  return paramsArrToCheck;
}

/**
 * Main validation function
 * @param {string} link - The URL to validate
 * @param {string} linkType - One of: 'Tracking_Link', 'Impression_Link', 'VTA_Link', 'Deep_Link'
 * @param {object} [options] - Optional configuration
 * @param {array} [options.linkValidatorData] - Pass link_validator_data manually
 * @param {boolean} [options.s2sTrackingEnabled] - S2S tracking flag
 * @returns {{ isValid: boolean, error?: string, trackingCompany?: number }}
 */
function validateLink(link, linkType, options = {}) {
  const { linkValidatorData: providedData, s2sTrackingEnabled: providedS2s } = options;

  // Validate linkType
  if (!VALID_LINK_TYPES.includes(linkType)) {
    return {
      isValid: false,
      error: `Invalid linkType. Must be one of: ${VALID_LINK_TYPES.join(', ')}`,
    };
  }

  // Get data
  const linkValidatorData = getLinkValidatorDataInternal(providedData);
  const s2sTrackingEnabled = getS2sTrackingEnabledInternal(providedS2s);

  if (!linkValidatorData) {
    return {
      isValid: false,
      error:
        'Link validator data not available. Please load creative data first or provide linkValidatorData in options.',
    };
  }

  // Empty check
  if (!link) {
    return { isValid: false, error: 'URL is required' };
  }

  try {
    const url = new URL(link.toLowerCase());

    // HTTPS check
    if (!url.protocol.includes('https:')) {
      return { isValid: false, error: 'URL must start with https://' };
    }

    // Domain format validation
    if (!validateDomainFormat(url.hostname)) {
      return { isValid: false, error: 'Invalid domain format' };
    }

    // Get domains for this link type
    const domainsObj = getDomainsObj(linkType, linkValidatorData);
    const domainsArr = domainsObj.map((item) => item.tc_domain_name);
    const validationIsRequire = domainsObj.some((item) => item.validationIsRequire);

    if (validationIsRequire) {
      // Find tracking company
      const trackingCompany = findTrackingCompany(url, linkType, linkValidatorData);

      if (!trackingCompany && !s2sTrackingEnabled) {
        return {
          isValid: false,
          error: 'Domain not recognized. Custom s2s attribution is not allowed.',
        };
      }

      // Check if domain is in allowed domains
      const isInMainDomains = domainsArr.some(
        (domain) => url.host.includes(domain) || domain.includes(url.host),
      );

      if (isInMainDomains) {
        // Get mandatory parameters
        const paramsArrToCheck = getValidateParams(url.host, linkValidatorData, linkType);

        if (paramsArrToCheck && paramsArrToCheck.length > 0) {
          const splitedParams = paramsArrToCheck.map((param) => {
            return param?.map((str) => str.split('='));
          });

          for (const params of splitedParams) {
            for (const [key, value] of params) {
              if (key) {
                // Use decodeURIComponent for key (commit 907f801f fix)
                const containValue = url.searchParams.get(decodeURIComponent(key));

                if (!containValue) {
                  return {
                    isValid: false,
                    error: `Missing mandatory parameter(s): ${key}=${value}`,
                  };
                }

                // Validate parameter value
                const shouldBe = splitedParams
                  .flatMap((param) =>
                    param?.filter((item) => item[0] === key)?.map((item) => item[1]),
                  )
                  .filter((item) => item && item.length);

                if (!shouldBe.includes(containValue)) {
                  return {
                    isValid: false,
                    error: `Missing mandatory parameter(s): ${key}=${value}`,
                  };
                }
              } else {
                // Handle params without key (macros check)
                let shouldBe2 = splitedParams
                  .map((param) => param?.filter((item) => !item[0])?.map((item) => item[1]))
                  .filter((item) => item && item.length);

                shouldBe2 = shouldBe2.reduce((acc, item) => {
                  acc = { ...acc, [item[0]]: [...item] };
                  return acc;
                }, {});

                let subStrNotContained = '';
                for (const strToOutput in shouldBe2) {
                  const macrosArray = shouldBe2[strToOutput];
                  const res = macrosArray.every((macros) => !url.href.includes(macros));
                  if (res) subStrNotContained = strToOutput;
                }

                if (subStrNotContained) {
                  return {
                    isValid: false,
                    error: `URL should contain ${subStrNotContained}`,
                  };
                }
              }
            }
          }
        }
      }

      return { isValid: true, trackingCompany: trackingCompany || 1 };
    }

    // Validation not required, but still find tracking company if possible
    const trackingCompany = findTrackingCompany(url, linkType, linkValidatorData);
    return { isValid: true, trackingCompany };
  } catch (e) {
    return { isValid: false, error: 'Invalid URL format' };
  }
}

/**
 * Get all valid domains for a link type
 * @param {string} linkType - One of: 'Tracking_Link', 'Impression_Link', 'VTA_Link', 'Deep_Link'
 * @param {array} [linkValidatorData] - Optional: pass data manually
 * @returns {string[]}
 */
function getValidDomains(linkType, linkValidatorData) {
  if (!VALID_LINK_TYPES.includes(linkType)) {
    return [];
  }

  const data = getLinkValidatorDataInternal(linkValidatorData);
  if (!data) return [];

  const domainsObj = getDomainsObj(linkType, data);
  return domainsObj.map((item) => item.tc_domain_name);
}

/**
 * Get mandatory parameters for a domain
 * @param {string} domain - The domain to get params for
 * @param {string} linkType - The link type
 * @param {array} [linkValidatorData] - Optional: pass data manually
 * @returns {Array<{key: string, value: string, description: string}>}
 */
function getMandatoryParams(domain, linkType, linkValidatorData) {
  if (!VALID_LINK_TYPES.includes(linkType)) {
    return [];
  }

  const data = getLinkValidatorDataInternal(linkValidatorData);
  if (!data || !domain) return [];

  const currentValidator = data.find((item) => {
    let domainsArr =
      item[validator_domains_field[linkType]] === null
        ? item[extra_validator_domains_field[linkType]]
        : item[validator_domains_field[linkType]];
    domainsArr = domainsArr?.map((d) => d.tc_domain_name);
    return domainsArr?.some((d) => d.includes(domain) || domain.includes(d));
  });

  if (!currentValidator) return [];

  const paramsArr =
    currentValidator[validator_domains_field[linkType]] !== null
      ? currentValidator[`${isValidField[linkType]}_params_android`]
      : currentValidator[`${exstraIsValidField[linkType]}_params_android`];

  if (!paramsArr) return [];

  return paramsArr
    .filter((param) => param.tcp_is_mandatory)
    .map((param) => {
      const firstValue =
        param.param_values?.find((v) => v.tcp_version === 1) || param.param_values?.[0];
      return {
        key: firstValue?.tcp_params?.split('=')?.[0] || '',
        value: firstValue?.tcp_params?.split('=')?.[1] || firstValue?.tcp_params || '',
        description: firstValue?.tcp_description || '',
        displayText: firstValue?.tcp_param_display_text || '',
      };
    });
}

/**
 * Check if link_validator_data is loaded in Redux store
 * @returns {boolean}
 */
function isDataLoaded() {
  if (!_store) return false;
  const data = _store.getState()?.creatives?.creativeAndTrackingData?.link_validator_data;
  return Array.isArray(data) && data.length > 0;
}

/**
 * Get current link_validator_data from Redux store
 * @returns {array|null}
 */
function getLinkValidatorData() {
  if (!_store) return null;
  return _store.getState()?.creatives?.creativeAndTrackingData?.link_validator_data || null;
}

/**
 * Initialize the Link Validator API with Redux store reference
 * @param {object} store - Redux store instance
 */
export function initLinkValidatorAPI(store) {
  _store = store;

  window.LinkValidator = {
    validateLink,
    getValidDomains,
    getMandatoryParams,
    isDataLoaded,
    getLinkValidatorData,
    // Expose valid link types for reference
    LINK_TYPES: VALID_LINK_TYPES,
  };
}
